import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import UsersList from './User/UsersList';
import {
  fetchUsers,
  deleteUser,
  clearError,
} from '../store/slices/usersSlice';
import { fetchRoles } from '../store/slices/rolesSlice';
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from 'sweetalert2';
import { selectHasPermission } from '../store/slices/authSlice';
import { getApiErrorMessage } from "../utils/apiError";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { users, isLoading, error } = useSelector((state) => state.users);
  const canManageUsers = useSelector((state) =>
    selectHasPermission(state, 'users-manage')
  );
  const canCreateUsers = useSelector((state) =>
    selectHasPermission(state, 'users-create')
  );
  const canEditUsers = useSelector((state) =>
    selectHasPermission(state, 'users-edit')
  );
  const canDeleteUsers = useSelector((state) =>
    selectHasPermission(state, 'users-delete')
  );

  useEffect(() => {
    if (canManageUsers) {
      dispatch(fetchUsers());
      dispatch(fetchRoles());
    }
  }, [dispatch, canManageUsers]);

  useEffect(() => {
    if (error) console.error('Users error:', error);
  }, [error]);

  if (!canManageUsers) {
    return (
      <Navigate
        to="/dashboard"
        state={{ error: 'You do not have permission to manage users.' }}
        replace
      />
    );
  }

  const handleAddUser = () => navigate('/users/add');
  const handleEditUser = (id) => navigate(`/users/edit/${id}`);
  const handleAssignRoles = (id) => navigate(`/users/assign-roles/${id}`);

  const handleDeleteUser = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You want to delete this user?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteUser(id)).unwrap();
        dispatch(fetchUsers());
        Swal.fire('Deleted!', 'User has been deleted.', 'success');
      } catch (err) {
        console.error('Failed to delete user:', err);
        const msg = getApiErrorMessage(err, 'Failed to delete user.');
        Swal.fire('Error!', msg, 'error');
      }
    }
  };
  return (
    <MainLayout>

      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        {/* ===== Error Alert ===== */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => dispatch(clearError())}
            ></button>
          </div>
        )}

        {/* ===== Users Table ===== */}
        <div className="card">
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <UsersList
                users={users}
                onEdit={canEditUsers ? handleEditUser : null}
                onDelete={canDeleteUsers ? handleDeleteUser : null}
                onAssignRoles={canEditUsers ? handleAssignRoles : null}
                onAddUser={canCreateUsers ? handleAddUser : null}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Users;
